import * as THREE from 'three';
import {
  GUARD_AIM_MARKER_COLOR_HEX,
  GUARD_AIM_MARKER_RADIUS_M,
  GUARD_ENEMY_BELT_FRAC,
  GUARD_ENEMY_DISTANCE_M,
  GUARD_ENEMY_EYE_FRAC,
  GUARD_ENEMY_WIDTH_FRAC,
  UI_INK,
  UI_INK_MUTED,
  UI_IVORY,
} from '../config/constants';

const CANVAS_W = 512;
const CANVAS_H = 1024;

/**
 * Figura del nemico (VG4 + R-revisione): the opponent reference in guard mode. A stylized fencer
 * on a plane, **as tall as the user**, placed in front at misura and billboarded to keep its eyes
 * on the user. Exposes its eye/belt world points (the directrix anchor + invito targets) and a live
 * aim marker showing where the user's blade ray lands. It is a reference, not a target to hit.
 */
export class EnemyFigure {
  /** Root added to the scene by the runtime: holds the billboard plane + the world-space aim marker. */
  readonly group = new THREE.Group();
  /** Eye / belt world points — updated by `place`. Read by the recognizer each frame. */
  readonly eyes = new THREE.Vector3();
  readonly belt = new THREE.Vector3();

  private readonly plane: THREE.Mesh;
  private readonly marker: THREE.Mesh;
  private readonly texture: THREE.CanvasTexture;
  private readonly heightM: number;
  private readonly camPos = new THREE.Vector3();
  private readonly forward = new THREE.Vector3();

  constructor(userHeightCm: number) {
    this.heightM = userHeightCm / 100;
    const widthM = this.heightM * GUARD_ENEMY_WIDTH_FRAC;

    this.texture = new THREE.CanvasTexture(drawEnemy());
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.plane = new THREE.Mesh(
      new THREE.PlaneGeometry(widthM, this.heightM),
      new THREE.MeshBasicMaterial({
        map: this.texture,
        transparent: true,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );
    this.group.add(this.plane);

    this.marker = new THREE.Mesh(
      new THREE.SphereGeometry(GUARD_AIM_MARKER_RADIUS_M, 12, 8),
      new THREE.MeshBasicMaterial({ color: GUARD_AIM_MARKER_COLOR_HEX, depthTest: false }),
    );
    this.marker.renderOrder = 999; // draw over the plane
    this.marker.visible = false;
    this.group.add(this.marker);
  }

  /** Stand the figure in front at misura, feet on the floor; cache the eye/belt world points. */
  place(camera: THREE.Camera): void {
    camera.getWorldPosition(this.camPos);
    camera.getWorldDirection(this.forward);
    this.forward.y = 0;
    if (this.forward.lengthSq() < 1e-4) this.forward.set(0, 0, -1);
    this.forward.normalize();
    const x = this.camPos.x + this.forward.x * GUARD_ENEMY_DISTANCE_M;
    const z = this.camPos.z + this.forward.z * GUARD_ENEMY_DISTANCE_M;
    this.plane.position.set(x, this.heightM / 2, z);
    this.eyes.set(x, this.heightM * GUARD_ENEMY_EYE_FRAC, z);
    this.belt.set(x, this.heightM * GUARD_ENEMY_BELT_FRAC, z);
  }

  /** Yaw-billboard toward the user each frame (eyes stay on them). */
  update(camera: THREE.Camera): void {
    camera.getWorldPosition(this.camPos);
    this.plane.lookAt(this.camPos.x, this.plane.position.y, this.camPos.z);
  }

  /** Move the live aim marker to where the blade ray lands (world), or hide it when there's no aim. */
  setAim(point: THREE.Vector3 | null): void {
    if (point) {
      this.marker.position.copy(point);
      this.marker.visible = true;
    } else {
      this.marker.visible = false;
    }
  }

  dispose(): void {
    this.group.parent?.remove(this.group);
    this.plane.geometry.dispose();
    (this.plane.material as THREE.Material).dispose();
    this.marker.geometry.dispose();
    (this.marker.material as THREE.Material).dispose();
    this.texture.dispose();
  }
}

/** Draw the fencer silhouette (ink, with eyes) plus the invito target bands (eyes / belt, dx|sx). */
function drawEnemy(): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;
  const c = canvas.getContext('2d');
  if (!c) throw new Error('2d canvas context unavailable');
  const cx = CANVAS_W / 2;
  // Canvas height maps 1:1 to body height (0 = floor, 1 = top of head); use it for every landmark
  // so the silhouette is exactly as tall as the invito bands and its eyes/belt sit on them.
  const py = (frac: number): number => (1 - frac) * CANVAS_H;

  // Invito target bands: faint strips at eye and belt height, split dx|sx by a centre line.
  const bandH = 70;
  const drawBand = (frac: number): void => {
    const y = py(frac);
    c.fillStyle = 'rgba(185,74,42,0.16)'; // UI_SEAL_RED, faint
    c.fillRect(0, y - bandH / 2, CANVAS_W, bandH);
    c.strokeStyle = 'rgba(36,20,8,0.35)';
    c.lineWidth = 2;
    c.beginPath();
    c.moveTo(0, y - bandH / 2);
    c.lineTo(CANVAS_W, y - bandH / 2);
    c.moveTo(0, y + bandH / 2);
    c.lineTo(CANVAS_W, y + bandH / 2);
    c.stroke();
  };
  drawBand(GUARD_ENEMY_EYE_FRAC);
  drawBand(GUARD_ENEMY_BELT_FRAC);
  // Centre divider (porte | code).
  c.strokeStyle = 'rgba(36,20,8,0.3)';
  c.lineWidth = 2;
  c.setLineDash([10, 10]);
  c.beginPath();
  c.moveTo(cx, 0);
  c.lineTo(cx, CANVAS_H);
  c.stroke();
  c.setLineDash([]);

  // Proportional fencer: every vertical landmark is a fraction of body height, so eyes land on the
  // eye band, hips on the belt band, and the feet reach the floor regardless of the user's height.
  const eyeY = py(GUARD_ENEMY_EYE_FRAC);
  const shoulderY = py(0.82);
  const beltY = py(GUARD_ENEMY_BELT_FRAC);
  const crotchY = py(0.46);
  const kneeY = py(0.27);
  const footY = py(0);
  const headR = 0.06 * CANVAS_H;

  // Torso + legs silhouette.
  c.fillStyle = UI_INK;
  c.strokeStyle = UI_INK;
  c.lineJoin = 'round';
  c.lineCap = 'round';
  c.beginPath();
  c.moveTo(cx - 85, shoulderY);
  c.lineTo(cx + 85, shoulderY);
  c.lineTo(cx + 58, beltY);
  c.lineTo(cx + 95, kneeY);
  c.lineTo(cx + 112, footY);
  c.lineTo(cx + 48, footY);
  c.lineTo(cx, crotchY);
  c.lineTo(cx - 48, footY);
  c.lineTo(cx - 112, footY);
  c.lineTo(cx - 95, kneeY);
  c.lineTo(cx - 58, beltY);
  c.closePath();
  c.fill();

  // Lead arm (toward the user's left), upper + fore.
  c.lineWidth = 28;
  c.beginPath();
  c.moveTo(cx - 70, shoulderY + 20);
  c.lineTo(cx - 175, shoulderY - 10);
  c.stroke();
  c.lineWidth = 11;
  c.beginPath();
  c.moveTo(cx - 175, shoulderY - 10);
  c.lineTo(cx - 270, shoulderY - 80);
  c.stroke();

  // Head + eyes (centred on the eye band).
  c.beginPath();
  c.arc(cx, eyeY - 4, headR, 0, Math.PI * 2);
  c.fill();
  c.fillStyle = UI_IVORY;
  c.beginPath();
  c.arc(cx - 22, eyeY - 6, 9, 0, Math.PI * 2);
  c.arc(cx + 22, eyeY - 6, 9, 0, Math.PI * 2);
  c.fill();

  // Floor shadow.
  c.fillStyle = UI_INK_MUTED;
  c.globalAlpha = 0.4;
  c.beginPath();
  c.ellipse(cx, footY - 8, 150, 20, 0, 0, Math.PI * 2);
  c.fill();
  c.globalAlpha = 1;

  return canvas;
}
